import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faPause,
  faPlay,
  faPlus,
  faStop,
  faTrash,
  faTriangleExclamation,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../hooks/useAuth";
import { useTimer } from "../context/TimerContext";
import {
  corrigirApontamento,
  lancarApontamento,
  listarApontamentos,
  removerApontamento,
} from "../api/taskTimeApi";
import {
  compararComEstimativa,
  formatarCronometro,
  formatarDataCurta,
  formatarDuracao,
  formatarHorarioCurto,
  parsearMinutos,
} from "../utils/taskTime";
import "./TaskTimePanel.css";

const ATALHOS_MINUTOS = [15, 30, 60];

/**
 * Controle de tempo de uma tarefa.
 *
 * Pausar e parar gravam a mesma coisa: fecham o segmento aberto. O total e a
 * soma dos segmentos, entao "pausei e voltei" e apenas dois segmentos - e por
 * isso que fechar o navegador no meio nao perde nada, e um apontamento errado
 * pode ser corrigido sozinho sem afetar o resto.
 */
const TaskTimePanel = ({ task, estimativaMinutos, onEstimativaChange }) => {
  const { user } = useAuth();
  const { emAndamento, segundos, ocupado, alternar, parar, ultimaTroca, limparAviso, versao } =
    useTimer();

  const [registros, setRegistros] = useState([]);
  const [totais, setTotais] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [mostrarManual, setMostrarManual] = useState(false);
  const [duracaoManual, setDuracaoManual] = useState("");
  const [observacaoManual, setObservacaoManual] = useState("");
  const [salvandoManual, setSalvandoManual] = useState(false);

  const [editando, setEditando] = useState(null);
  const [duracaoEdicao, setDuracaoEdicao] = useState("");

  const usuarioId = user?.id ?? user?.userId ?? null;
  const rodandoAqui = Boolean(emAndamento) && Number(emAndamento.id_tarefa) === Number(task?.id);

  const carregar = useCallback(async () => {
    if (!task?.id) return;

    setCarregando(true);

    try {
      const { registros: lista, totais: somas } = await listarApontamentos(task.id);
      setRegistros(lista);
      setTotais(somas);
      setErro("");
    } catch (error) {
      console.error("TaskTimePanel: erro ao carregar apontamentos", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao carregar o tempo.");
    } finally {
      setCarregando(false);
    }
  }, [task?.id]);

  // `versao` muda a cada start/stop, inclusive os disparados pela lista atras
  // do modal ou pelo widget do topo.
  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  useEffect(() => () => limparAviso(), [limparAviso]);

  /**
   * Total vivo: o servidor conta o segmento aberto ate o instante da resposta,
   * entao trocamos aquela parcela pelo que o relogio local ja marcou.
   */
  const totalSegundos = useMemo(() => {
    const base = Number(totais?.segundos_total || 0);
    if (!rodandoAqui) return base;

    const registrado = registros.find((item) => item.id === emAndamento.id);
    return base - Number(registrado?.segundos || 0) + segundos;
  }, [totais, registros, rodandoAqui, emAndamento, segundos]);

  const comparacao = compararComEstimativa(totalSegundos, estimativaMinutos);

  const porUsuario = totais?.por_usuario || [];
  const temTimeInteiro = porUsuario.length > 1;

  async function lancarManual() {
    const minutos = parsearMinutos(duracaoManual);

    if (!minutos || minutos <= 0) {
      setErro('Nao entendi a duracao. Use algo como "45", "1h30" ou "2:15".');
      return;
    }

    setSalvandoManual(true);
    setErro("");

    try {
      await lancarApontamento(task.id, { minutos, observacao: observacaoManual });
      setDuracaoManual("");
      setObservacaoManual("");
      setMostrarManual(false);
      await carregar();
    } catch (error) {
      console.error("TaskTimePanel: erro ao lancar tempo", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao lancar o tempo.");
    } finally {
      setSalvandoManual(false);
    }
  }

  async function salvarCorrecao(registro) {
    const minutos = parsearMinutos(duracaoEdicao);

    if (!minutos || minutos <= 0) {
      setErro('Nao entendi a duracao. Use algo como "45", "1h30" ou "2:15".');
      return;
    }

    try {
      await corrigirApontamento(registro.id, { minutos });
      setEditando(null);
      await carregar();
    } catch (error) {
      console.error("TaskTimePanel: erro ao corrigir apontamento", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao corrigir o apontamento.");
    }
  }

  async function remover(registro) {
    if (!window.confirm(`Remover o apontamento de ${formatarDuracao(registro.segundos)}?`)) return;

    try {
      await removerApontamento(registro.id);
      await carregar();
    } catch (error) {
      console.error("TaskTimePanel: erro ao remover apontamento", error);
      setErro(error?.response?.data?.message || error?.message || "Erro ao remover o apontamento.");
    }
  }

  return (
    <div className="ttp">
      <div className="ttp-head">
        <h5>Controle de tempo</h5>

        {totais && (
          <span className="ttp-total-chip" title="Somando a checklist desta tarefa">
            {formatarDuracao(totalSegundos)}
            {estimativaMinutos > 0 && (
              <em> / {formatarDuracao(Number(estimativaMinutos) * 60)} previsto</em>
            )}
          </span>
        )}
      </div>

      {/* ─── Cronometro ───────────────────────────────────────────────────── */}
      <div className={`ttp-cronometro ${rodandoAqui ? "rodando" : ""}`}>
        <div className="ttp-relogio">
          <strong>{formatarCronometro(rodandoAqui ? segundos : 0)}</strong>
          <small>
            {rodandoAqui
              ? `Iniciado as ${formatarHorarioCurto(emAndamento.inicio)}`
              : totalSegundos > 0
              ? "Cronometro parado"
              : "Nenhum tempo apontado ainda"}
          </small>
        </div>

        <div className="ttp-botoes">
          <Button
            variant={rodandoAqui ? "warning" : "success"}
            disabled={ocupado || !task?.id}
            onClick={() => alternar(task)}
          >
            <FontAwesomeIcon icon={rodandoAqui ? faPause : faPlay} />{" "}
            {rodandoAqui ? "Pausar" : totalSegundos > 0 ? "Retomar" : "Iniciar"}
          </Button>

          {/* Parar existe separado de pausar por intencao, nao por gravacao:
              os dois fecham o segmento, mas "parar" e o gesto de encerrar. */}
          <Button
            variant="outline-danger"
            disabled={ocupado || !rodandoAqui}
            onClick={parar}
            title="Encerrar a sessao de trabalho"
          >
            <FontAwesomeIcon icon={faStop} /> Parar
          </Button>

          <Button
            variant="outline-secondary"
            onClick={() => setMostrarManual((atual) => !atual)}
            title="Lancar tempo trabalhado sem cronometro"
          >
            <FontAwesomeIcon icon={faPlus} /> Lancar tempo
          </Button>
        </div>
      </div>

      {emAndamento && !rodandoAqui && (
        <p className="ttp-aviso">
          <FontAwesomeIcon icon={faTriangleExclamation} /> O cronometro esta rodando em{" "}
          <strong>{emAndamento.tarefa}</strong>. Iniciar aqui pausa aquele automaticamente.
        </p>
      )}

      {ultimaTroca && rodandoAqui && (
        <p className="ttp-aviso ok">
          <FontAwesomeIcon icon={faWandMagicSparkles} /> Pausei <strong>{ultimaTroca.tarefa}</strong>{" "}
          com {formatarDuracao(ultimaTroca.segundos)} para comecar esta.
        </p>
      )}

      {/* ─── Estimativa ───────────────────────────────────────────────────── */}
      <div className="ttp-estimativa">
        <Form.Label htmlFor="ttp-estimativa-campo">Estimativa</Form.Label>
        <div className="ttp-estimativa-campo">
          <Form.Control
            id="ttp-estimativa-campo"
            type="number"
            min="0"
            step="15"
            placeholder="minutos"
            value={estimativaMinutos ?? ""}
            onChange={(evento) =>
              onEstimativaChange?.(evento.target.value === "" ? "" : Number(evento.target.value))
            }
          />
          <span className="ttp-estimativa-atalhos">
            {ATALHOS_MINUTOS.map((minutos) => (
              <button key={minutos} type="button" onClick={() => onEstimativaChange?.(minutos)}>
                {minutos}min
              </button>
            ))}
            <button type="button" onClick={() => onEstimativaChange?.(480)}>
              1 dia
            </button>
          </span>
        </div>

        {comparacao && (
          <div className={`ttp-barra ${comparacao.estourou ? "estourou" : ""}`}>
            <div
              className="ttp-barra-preenchida"
              style={{ width: `${Math.min(100, comparacao.percentual)}%` }}
            />
            <span>
              {comparacao.percentual}% do previsto
              {comparacao.estourou
                ? ` · ${formatarDuracao(totalSegundos - comparacao.previstos)} acima`
                : ` · restam ${formatarDuracao(comparacao.restanteSegundos)}`}
            </span>
          </div>
        )}

        <Form.Text className="text-muted">
          A estimativa e salva junto com a tarefa, ao confirmar o modal.
        </Form.Text>
      </div>

      {/* ─── Lancamento manual ────────────────────────────────────────────── */}
      {mostrarManual && (
        <div className="ttp-manual">
          <Form.Control
            autoFocus
            placeholder='Quanto tempo? Ex.: 45, 1h30, 2:15'
            value={duracaoManual}
            onChange={(evento) => setDuracaoManual(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === "Enter") {
                evento.preventDefault();
                lancarManual();
              }
            }}
          />
          <Form.Control
            placeholder="O que foi feito? (opcional)"
            value={observacaoManual}
            onChange={(evento) => setObservacaoManual(evento.target.value)}
          />
          <Button onClick={lancarManual} disabled={salvandoManual}>
            {salvandoManual ? <Spinner size="sm" /> : "Lancar"}
          </Button>
        </div>
      )}

      {erro && <p className="ttp-erro">{erro}</p>}

      {/* ─── Quem trabalhou ───────────────────────────────────────────────── */}
      {temTimeInteiro && (
        <div className="ttp-por-usuario">
          {porUsuario.map((linha) => (
            <span key={linha.id_usuario} className="ttp-por-usuario-item">
              {linha.usuario_nome || `Usuario ${linha.id_usuario}`}
              <strong>{formatarDuracao(linha.segundos)}</strong>
            </span>
          ))}
        </div>
      )}

      {/* ─── Apontamentos ─────────────────────────────────────────────────── */}
      {carregando ? (
        <div className="ttp-carregando">
          <Spinner size="sm" />
        </div>
      ) : registros.length === 0 ? (
        <p className="ttp-vazio">
          Nenhum apontamento ainda. Use o cronometro ou lance o tempo que ja foi gasto.
        </p>
      ) : (
        <ul className="ttp-lista">
          {registros.map((registro) => {
            const meu = Number(registro.id_usuario) === Number(usuarioId);
            const daChecklist = Number(registro.id_tarefa) !== Number(task?.id);
            const emEdicao = editando === registro.id;

            return (
              <li key={registro.id} className={`ttp-item ${registro.rodando ? "rodando" : ""}`}>
                <span className="ttp-item-quando">
                  {formatarDataCurta(registro.inicio)} · {formatarHorarioCurto(registro.inicio)}
                  {registro.fim && ` – ${formatarHorarioCurto(registro.fim)}`}
                </span>

                <span className="ttp-item-desc">
                  {/* Um passo da checklist entra no total do pai; sem a trilha,
                      o total do modal pareceria maior que os apontamentos. */}
                  {daChecklist && <em className="ttp-item-origem">{registro.tarefa} ›</em>}
                  {registro.observacao || (registro.manual ? "Lancamento manual" : "Cronometro")}
                  {!meu && <span className="ttp-item-dono">{registro.usuario_nome}</span>}
                </span>

                {emEdicao ? (
                  <span className="ttp-item-edicao">
                    <Form.Control
                      autoFocus
                      size="sm"
                      value={duracaoEdicao}
                      onChange={(evento) => setDuracaoEdicao(evento.target.value)}
                      onKeyDown={(evento) => {
                        if (evento.key === "Enter") {
                          evento.preventDefault();
                          salvarCorrecao(registro);
                        }
                        if (evento.key === "Escape") setEditando(null);
                      }}
                    />
                    <button type="button" onClick={() => salvarCorrecao(registro)} title="Salvar">
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ttp-item-tempo"
                    disabled={!meu || registro.rodando}
                    title={
                      registro.rodando
                        ? "Em andamento"
                        : meu
                        ? "Corrigir a duracao"
                        : "So quem lancou pode corrigir"
                    }
                    onClick={() => {
                      setEditando(registro.id);
                      setDuracaoEdicao(String(Math.round(registro.segundos / 60)));
                    }}
                  >
                    {registro.rodando
                      ? formatarCronometro(meu && rodandoAqui ? segundos : registro.segundos)
                      : formatarDuracao(registro.segundos)}
                  </button>
                )}

                {meu && !registro.rodando && (
                  <button
                    type="button"
                    className="ttp-item-remover"
                    onClick={() => remover(registro)}
                    title="Remover apontamento"
                    aria-label="Remover apontamento"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TaskTimePanel;
